/**
 * The ONE Oracle "is this text an ANONYMOUS PL/SQL block" predicate, shared by
 * the renderer and the electron main process. Lives under `src/core/engines/`
 * (like `kustoUrl.ts`) precisely so both sides can import it: electron already
 * imports from `src/` in ~500 places, the renderer cannot import from
 * `electron/`, so `src/` is the only direction a single implementation can
 * live in.
 *
 * ## Why it is one function and not two
 *
 * It used to be two, with the same exported name, and they drifted (audit item
 * U5). `src/features/workspace/query-editor/utils/plsqlDetection.ts` gated the
 * Oracle **Debug** button; `electron/query-engine-adapters/oracleAdapterUtils.ts`
 * gated the adapter's DBMS_OUTPUT inline-capture wrap and (via `isPlsqlBlock`)
 * the trailing-`;` compensation. The old renderer docblock claimed the two were
 * "deliberately named differently" — they were not; both were called
 * `isAnonymousPlsqlBlock`, and the note was actually about the *wider*
 * `isPlsqlBlock`, which is a genuinely different question and still lives in
 * the adapter utils.
 *
 * The two callers phrase their question differently — "can the debugger step
 * through this" vs "can I nest this inside a wrapper block" — but both reduce
 * to the same physical property: **does this text survive being nested inside a
 * PL/SQL `BEGIN … END;` shell**. `startDebugSession`
 * (`electron/oracle-debugger/executionService.ts`) nests the editor text inside
 * exactly the same DBMS_OUTPUT capture wrapper the adapter builds. One property,
 * one predicate.
 *
 * ## Leading comments are part of the answer — third time asked
 *
 * A leading `--` / block comment in front of real SQL has now produced the same
 * bug three times in one wave:
 *
 * 1. **W1.4 fix-round-1** — `isPlsqlBlock` tested `startsWith('BEGIN'|'DECLARE')`
 *    on raw text. A generated schema-sync script puts a `-- [REVIEW] …`
 *    explanation directly above an Oracle sequence fix-up's block with no `;`
 *    between them, so `splitOracleStatements` correctly bundles comment + block
 *    into ONE statement whose text starts with `--`. The trailing-`;`
 *    compensation never fired and live Oracle answered ORA-06550/PLS-00103
 *    (confirmed live: `oracledb.execute()` requires the trailing `;` after the
 *    outermost `END` unconditionally, even for `BEGIN NULL; END`). Fixed by
 *    stripping leading comments first.
 * 2. **F7** — `def.toUpperCase().startsWith('CREATE')` in the schema-compare
 *    script generator/comparer answered `false` for a definition that opens
 *    with a banner comment (the reporter's own code style; 3 of the 41 modules
 *    in Microsoft's AdventureWorks open with one), producing a spurious
 *    "definition incomplete" marker. Fixed by converging both private gates
 *    into `electron/schema-compare/sqlHeaderPattern.ts`.
 * 3. **U5 (this module)** — the renderer copy was `/^\s*(DECLARE|BEGIN)\b/i`
 *    with no stripping at all, so `-- note\nBEGIN NULL; END;` — a block the
 *    debugger runs fine — got **no Debug button**, while the electron copy of
 *    the same-named function said yes.
 *
 * Occurrences 1 and 2 were fixed in place / converged within one process.
 * Occurrence 3 crossed the renderer/electron boundary, which is why the fix
 * here is a shared module rather than a second patched regex.
 * `oraclePlsqlDetection.test.ts` asserts that
 * `oracleAdapterUtils.isAnonymousPlsqlBlock` is *the same function object* as
 * this one, so re-introducing a private copy fails a test rather than shipping.
 *
 * ## Why `CREATE …` must answer false
 *
 * MEASURED against Oracle 26ai Free (23.26.1.0.0) rather than reasoned about,
 * because the wider `isPlsqlBlock` predicate makes the wrong answer look
 * plausible. `QueryEditorTab.handleDebugPlsql` opens the debugger with
 * `objectType: 'anonymous'` and the raw editor text as `source`, and
 * `startDebugSession` then takes one of two paths with it — neither of which
 * debugs a `CREATE …`:
 *
 * - no breakpoints queued (what a fresh Debug click always does): the source is
 *   nested inside the DBMS_OUTPUT capture wrapper, and every `CREATE …` shape
 *   raises **ORA-06550** there — DDL cannot appear inside a PL/SQL block. All
 *   four `CREATE` shapes this predicate used to match failed that way.
 * - breakpoints queued: the source runs bare, so the CREATE compiles as ordinary
 *   DDL and the session ends with nothing stepped and no breakpoint bound.
 *
 * So the button must not appear for `CREATE …` at all — widening it to the
 * shapes W1.8 taught the ADAPTER to run (TYPE, TYPE BODY, EDITIONABLE) would
 * have added three more broken buttons (#72). To debug a stored routine, run the
 * CREATE, then open the object from the Object Explorer: that path launches the
 * debugger with a real schema/name and a generated `BEGIN "s"."n"(); END;`
 * launcher, which is what DBMS_DEBUG can actually step through. The adapter has
 * the same exclusion for its own reason: nesting `CREATE … AS BEGIN … END;`
 * inside the DBMS_OUTPUT shell is a syntax error (ORA-06550, confirmed live).
 */

/**
 * Strip LEADING `--` line comments and block comments (and the whitespace
 * around them) off a SQL string. Only ever called on the START of a string that
 * has already been isolated as one statement — never mid-statement — so a
 * simple prefix loop is sufficient; no quote-awareness is needed here (nothing
 * before the first real token can be inside a string literal).
 *
 * Handles CRLF for free: a `--` comment ends at the `\n`, and the trailing
 * `\r` is eaten by the next iteration's leading-whitespace trim.
 */
export const stripLeadingComments = (sql: string): string => {
  let s = sql;
  for (;;) {
    const withoutLeadingWs = s.replace(/^\s+/, '');
    if (withoutLeadingWs.startsWith('--')) {
      const newlineIndex = withoutLeadingWs.indexOf('\n');
      s = newlineIndex === -1 ? '' : withoutLeadingWs.slice(newlineIndex + 1);
      continue;
    }
    if (withoutLeadingWs.startsWith('/*')) {
      const closeIndex = withoutLeadingWs.indexOf('*/');
      s = closeIndex === -1 ? '' : withoutLeadingWs.slice(closeIndex + 2);
      continue;
    }
    return withoutLeadingWs;
  }
};

/**
 * `DECLARE`/`BEGIN` as the first real token. The `\b` matters: `BEGIN_DATE` is
 * an identifier, not the head of a block.
 */
const ANONYMOUS_PLSQL_PATTERN = /^(DECLARE|BEGIN)\b/i;

/**
 * Returns true if the given SQL text is an anonymous PL/SQL block — i.e. its
 * first real token, ignoring any leading comments, is `DECLARE` or `BEGIN`.
 * `CREATE … PROCEDURE/FUNCTION/…` routine DDL that merely *contains*
 * `BEGIN`/`END` answers false; see this module's header for why both callers
 * need that exclusion.
 */
export function isAnonymousPlsqlBlock(sql: string): boolean {
  if (!sql) return false;
  return ANONYMOUS_PLSQL_PATTERN.test(stripLeadingComments(sql));
}
